import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { useFirebaseImage } from '../../../Helpers/ImageLoader';
import { componentLocationMarkers, useComponentLocationImages } from '../../../hooks/workOrders/useComponentLocationImages';
import { ComponentLocations } from '../../WorkOrders/components/shared';
import { useInView } from '../../../hooks/useInView';

type LocationMarker = ReturnType<typeof componentLocationMarkers>[number];

// Small image with the component's pin. Drawings and satellite images have no
// resized variants, so the full image is loaded (once per image, lazily)
const LocationThumbnail: React.FC<{ marker: LocationMarker; onClick: () => void }> = ({ marker, onClick }) => {
    const { ref, inView } = useInView<HTMLButtonElement>();
    const image = useFirebaseImage(inView ? marker.storagePath : null);
    const hasFrame = marker.refWidth > 0 && marker.refHeight > 0;

    return (
        <button
            ref={ref}
            type="button"
            className="validation-location"
            style={hasFrame ? { aspectRatio: `${marker.refWidth} / ${marker.refHeight}` } : undefined}
            onClick={onClick}
            title={marker.label}
            aria-label={`Visa position: ${marker.label}`}
        >
            {inView && !image.isLoading && !image.hasError && image.src ? (
                <>
                    <img src={image.src} alt="" />
                    {hasFrame && (
                        <span
                            aria-hidden
                            className="validation-location-pin"
                            style={{
                                left: `${(marker.x / marker.refWidth) * 100}%`,
                                top: `${(marker.y / marker.refHeight) * 100}%`,
                            }}
                        />
                    )}
                </>
            ) : (
                <span className="thumbnail-placeholder" />
            )}
        </button>
    );
};

const LocationsModal: React.FC<{
    component: BaseComponentImpl;
    building: Building;
    onClose: () => void;
}> = ({ component, building, onClose }) => {
    const components = useMemo(() => ({ [component.key]: component }), [component]);
    const keys = useMemo(() => [component.key], [component.key]);
    const locations = useComponentLocationImages(building, components, keys)[component.key] || [];
    const name = component.displayName(building);

    return (
        <Modal show onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Position – {name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ComponentLocations components={[{ key: component.key, name, locations }]} />
            </Modal.Body>
        </Modal>
    );
};

/**
 * Where the component is: its pins in parent photos, on the current level or
 * roof drawing and on the current satellite image, as small thumbnails. A click
 * opens them enlarged.
 */
export const ValidationLocations: React.FC<{
    component: BaseComponentImpl;
    building: Building;
}> = ({ component, building }) => {
    const [showModal, setShowModal] = useState(false);
    const markers = useMemo(() => componentLocationMarkers(component, building), [component, building]);

    if (markers.length === 0) {
        return <span className="placement-empty">–</span>;
    }

    return (
        <>
            {markers.map((marker, index) => (
                <LocationThumbnail
                    key={`${marker.storagePath}-${index}`}
                    marker={marker}
                    onClick={() => setShowModal(true)}
                />
            ))}
            {showModal && (
                <LocationsModal component={component} building={building} onClose={() => setShowModal(false)} />
            )}
        </>
    );
};
